import React, { useState, useEffect } from "react";
import { Layout, Modal, Select } from "antd";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { axiosIntercept } from "http/axiosIntercept";
import { IDomainsConfig } from "pages/Ponds/types";
import Sidebar from "components/Sidebar";
import AppGallery from "components/AppGallery";
import AddOrEditSpace from "components/AddOrEditSpace";

const { Header, Content, Footer } = Layout;

interface TemplateInfo {
  gitUrl: string;
  domainName: string;
  profileType: string;
}

const GalleryPage: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const msalInstance = useMsal();

  // Register interceptor once; re-runs only if msalInstance identity changes
  useEffect(() => {
    axiosIntercept(msalInstance);
  }, [msalInstance]);

  const [availableDomains, setAvailableDomains] = useState<IDomainsConfig[]>([]);
  const [templateGitUrl, setTemplateGitUrl] = useState<string | null>(null);
  const [templateProfileType, setTemplateProfileType] = useState<string>("");
  const [domainModalVisible, setDomainModalVisible] = useState(false);
  const [domainModalSelection, setDomainModalSelection] = useState<string | undefined>(undefined);
  const [selectedTemplateInfo, setSelectedTemplateInfo] = useState<TemplateInfo | null>(null);

  // Analytics profiles may not use the External Data Exchange domain
  const ANALYTICS_RESTRICTED_PROFILES = new Set(["store_analyze", "ml_feature_eng", "ml_model_eng", "gen_ai_eng", "analytics"]);

  useEffect(() => {
    try {
      const cached = localStorage.getItem("domainsData");
      if (cached) {
        const domains: IDomainsConfig[] = JSON.parse(cached);
        setAvailableDomains(domains.filter((d) => d.active === "1"));
      }
    } catch {
      // ignore parse errors
    }
  }, []);

  const collapsedWidth = 50;
  const fullWidth = 300;

  const handleDomainSelect = (domain: IDomainsConfig) => {
    navigate(`/explorer/${domain.display_name}`);
  };

  const handleUseAsTemplate = (gitUrl: string, profileType: string) => {
    // Refresh domains from localStorage in case Sidebar has fetched them since mount
    try {
      const cached = localStorage.getItem("domainsData");
      if (cached) {
        const domains: IDomainsConfig[] = JSON.parse(cached);
        setAvailableDomains(domains.filter((d) => d.active === "1"));
      }
    } catch {
      // ignore
    }
    setTemplateGitUrl(gitUrl);
    setTemplateProfileType(profileType);
    setDomainModalSelection(undefined);
    setDomainModalVisible(true);
  };

  const handleDomainModalOk = () => {
    if (domainModalSelection && templateGitUrl) {
      setSelectedTemplateInfo({ gitUrl: templateGitUrl, domainName: domainModalSelection, profileType: templateProfileType });
      setDomainModalVisible(false);
      setTemplateGitUrl(null);
      setTemplateProfileType("");
      setDomainModalSelection(undefined);
    }
  };

  const handleDomainModalCancel = () => {
    setDomainModalVisible(false);
    setTemplateGitUrl(null);
    setTemplateProfileType("");
    setDomainModalSelection(undefined);
  };

  const handleTemplateSuccess = () => {
    const domainName = selectedTemplateInfo?.domainName;
    setSelectedTemplateInfo(null);
    if (domainName) navigate(`/explorer/${domainName}`);
  };

  const handleTemplateDrawerClose = () => {
    setSelectedTemplateInfo(null);
  };

  return (
    <Layout style={{ marginLeft: collapsed ? collapsedWidth : fullWidth }}>
      <Sidebar
        collapsed={collapsed}
        collapsedWidth={collapsedWidth}
        width={fullWidth}
        selectedDomainKey="gallery"
        onCollapse={setCollapsed}
        onDomainSelect={handleDomainSelect}
      />
      <Layout>
        <Header style={{ padding: 0 }} />
        <Content style={{ margin: "0 16px" }}>
          <div style={{ padding: 24, paddingBottom: 0, textAlign: "right", height: "calc(100vh - 64px - 70px)", overflowY: "auto" }}>
            <AppGallery onUseAsTemplate={handleUseAsTemplate} />
          </div>
        </Content>
        <Footer style={{ textAlign: "center" }} />
      </Layout>

      <Modal
        title="Select Domain"
        open={domainModalVisible}
        onOk={handleDomainModalOk}
        onCancel={handleDomainModalCancel}
        okText="Open Create Space"
        okButtonProps={{ disabled: !domainModalSelection }}
      >
        <p>Select the domain in which to create the new space:</p>
        <Select
          style={{ width: "100%" }}
          placeholder="Select domain"
          value={domainModalSelection}
          onChange={setDomainModalSelection}
          options={availableDomains.map((d) => ({
            label: d.display_name,
            value: d.display_name,
            disabled: ANALYTICS_RESTRICTED_PROFILES.has(templateProfileType.toLowerCase()) && d.display_name === "External Data Exchange",
          }))}
        />
      </Modal>

      {selectedTemplateInfo && (
        <AddOrEditSpace
          mode="create"
          autoTrigger
          domainName={selectedTemplateInfo.domainName}
          parentTemplateRepo={selectedTemplateInfo.gitUrl}
          galleryProfileType={selectedTemplateInfo.profileType}
          onSuccess={handleTemplateSuccess}
          onDrawerClose={handleTemplateDrawerClose}
        />
      )}
    </Layout>
  );
};

export default GalleryPage;
